import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { 
  UserGroupIcon, 
  CheckCircleIcon, 
  XCircleIcon, 
  ClockIcon,
  ArrowRightIcon 
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/common/Card';
import { Badge } from '../components/common/Badge';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { Textarea } from '../components/common/Textarea';
import Modal from '../components/common/Modal';
import { Alert } from '../components/common/Alert';
import toast from 'react-hot-toast';

interface Substitution {
  id: number;
  leave_request_id: number;
  user_id: number;
  substitute_id: number;
  status: 'pending' | 'accepted' | 'rejected' | 'cancelled';
  response_comment?: string;
  response_date?: string;
  area_of_responsibility?: string;
  priority: number;
  handover_notes?: string;
  created_at: string;
  updated_at: string;
  leaveRequest?: {
    id: number;
    start_date: string;
    end_date: string;
    total_days: number;
    reason?: string;
    leaveType?: {
      name: string;
    };
  };
  user?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    position?: string;
    department?: {
      id: number;
      name: string;
    };
    departments?: Array<{
      id: number;
      name: string;
    }>;
    teams?: Array<{
      id: number;
      name: string;
      department_id: number;
    }>;
  };
  substitute?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    position?: string;
    department?: {
      id: number;
      name: string;
    };
    departments?: Array<{
      id: number;
      name: string;
    }>;
    teams?: Array<{
      id: number;
      name: string;
      department_id: number;
    }>;
  };
}

const Substitutions: React.FC = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [filter, setFilter] = useState<'all' | 'pending' | 'accepted' | 'rejected'>('all');
  const [selectedSubstitution, setSelectedSubstitution] = useState<Substitution | null>(null);
  const [responseModalOpen, setResponseModalOpen] = useState(false);
  const [responseComment, setResponseComment] = useState('');

  const { data: substitutions, isLoading, error } = useQuery({
    queryKey: ['substitutions', filter],
    queryFn: async () => {
      try {
        console.log('Fetching substitutions with filter:', filter);
        const response = await api.get('/substitutions/my', {
          params: { 
            type: filter,
            include: 'departments,teams' // Include departments and teams data
          }
        });
        console.log('Substitutions response:', response.data);
        return response.data;
      } catch (error) {
        console.error('Substitutions API error:', error);
        // Try alternative endpoints
        try {
          const fallbackResponse = await api.get('/substitutions');
          console.log('Substitutions fallback response:', fallbackResponse.data);
          return fallbackResponse.data;
        } catch (fallbackError) {
          console.error('Substitutions fallback error:', fallbackError);
          // Return empty structure instead of throwing
          return { requests: [], responsibilities: [] };
        }
      }
    },
    retry: 1
  });

  const respondMutation = useMutation({
    mutationFn: async ({ id, status, comment }: { id: number; status: 'accepted' | 'rejected'; comment: string }) => {
      const response = await api.post(`/substitutions/${id}/respond`, {
        status,
        response_comment: comment
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['substitutions'] });
      toast.success(t('substitutionsPage.responseSuccess', 'Response sent successfully'));
      setResponseModalOpen(false);
      setSelectedSubstitution(null);
      setResponseComment('');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || t('substitutionsPage.responseError', 'Error sending response'));
    }
  });

  // Fetch departments data at the component level
  const { data: departments } = useQuery({
    queryKey: ['departments'],
    queryFn: async () => {
      const response = await api.get('/departments');
      return response.data.departments || [];
    }
  });

  // Helper function to get department from teams
  const getDepartmentFromTeams = (user: Substitution['user'] | Substitution['substitute']) => {
    if (!user?.teams || user.teams.length === 0) {
      return null;
    }
    
    // Find department based on team's department_id
    if (departments && user.teams[0].department_id) {
      const teamDepartment = departments.find((dept: any) => dept.id === user.teams[0].department_id);
      if (teamDepartment) {
        return { id: teamDepartment.id, name: teamDepartment.name };
      }
    }
    
    // Fallback if we can't find the actual department name
    return user.teams[0].department_id ? { id: user.teams[0].department_id, name: 'Department' } : null;
  };

  // Helper function to get the most relevant department for display
  const getUserDepartment = (user: Substitution['user'] | Substitution['substitute']) => {
    if (!user) return '';
    
    // Check for direct department assignment
    if (user.department?.name) {
      return user.department.name;
    }
    
    // Check for departments array
    if (user.departments && user.departments.length > 0) {
      return user.departments[0].name;
    }
    
    // Try to find department through team membership
    const teamDepartment = getDepartmentFromTeams(user);
    if (teamDepartment) {
      return teamDepartment.name;
    }
    
    return '';
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge color="yellow">{t('substitutions.status.pending')}</Badge>;
      case 'accepted':
        return <Badge color="green">{t('substitutions.status.accepted')}</Badge>;
      case 'rejected':
        return <Badge color="red">{t('substitutions.status.rejected')}</Badge>;
      case 'cancelled':
        return <Badge color="gray">{t('substitutions.status.cancelled')}</Badge>;
      default:
        return <Badge color="gray">{status}</Badge>;
    }
  };

  const handleRespond = (status: 'accepted' | 'rejected') => {
    if (selectedSubstitution) {
      respondMutation.mutate({
        id: selectedSubstitution.id,
        status,
        comment: responseComment
      });
    }
  };

  // Handle different response structures
  const substitutionRequests = substitutions?.requests || substitutions?.data?.requests || substitutions || [];
  const substitutionResponsibilities = substitutions?.responsibilities || substitutions?.data?.responsibilities || [];
  
  console.log('Processed substitution requests:', substitutionRequests);
  console.log('Processed substitution responsibilities:', substitutionResponsibilities);

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-semibold">{t('substitutions.title')}</h2>
        <p className="mt-1 text-sm text-gray-500">
          {t('substitutions.subtitle')}
        </p>
      </div>

      {/* Filter Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {['all', 'pending', 'accepted', 'rejected'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status as any)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                filter === status
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t(`substitutions.filters.${status}`)}
            </button>
          ))}
        </nav>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* My Requests for Substitutes */}
          <Card>
            <CardHeader>
              <CardTitle>{t('substitutions.sections.myRequests')}</CardTitle>
            </CardHeader>
            <CardContent>
              {substitutionRequests.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  {t('substitutions.noRequests')}
                </p>
              ) : (
                <div className="space-y-4">
                  {substitutionRequests.map((sub: Substitution) => (
                    <div
                      key={sub.id}
                      className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <UserGroupIcon className="h-5 w-5 text-gray-400" />
                            <span className="font-medium">
                              {sub.substitute?.first_name} {sub.substitute?.last_name}
                            </span>
                            <ArrowRightIcon className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-600">
                              {t('substitutions.substitute')}
                            </span>
                            {getUserDepartment(sub.substitute) && (
                              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                                {getUserDepartment(sub.substitute)}
                              </span>
                            )}
                          </div>
                          {sub.substitute?.teams && sub.substitute.teams.length > 0 && (
                            <div className="ml-7 mb-2 flex flex-wrap gap-1">
                              {sub.substitute.teams.map(team => (
                                <span key={team.id} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                  {team.name}
                                </span>
                              ))}
                            </div>
                          )}
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-gray-500">{t('substitutionsPage.period')}</p>
                              <p className="font-medium">
                                {format(new Date(sub.leaveRequest?.start_date || ''), 'MMM d, yyyy')} - 
                                {format(new Date(sub.leaveRequest?.end_date || ''), 'MMM d, yyyy')}
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-500">{t('substitutions.leaveType')}</p>
                              <p className="font-medium">
                                {sub.leaveRequest?.leaveType?.name || 'N/A'}
                              </p>
                            </div>
                          </div>
                          {sub.area_of_responsibility && (
                            <div className="mt-2">
                              <p className="text-gray-500 text-sm">{t('substitutions.areaOfResponsibility')}</p>
                              <p className="text-sm">{sub.area_of_responsibility}</p>
                            </div>
                          )}
                          {sub.response_comment && (
                            <div className="mt-2">
                              <p className="text-gray-500 text-sm">{t('substitutions.response')}</p>
                              <p className="text-sm italic">{sub.response_comment}</p>
                            </div>
                          )}
                        </div>
                        <div>
                          {getStatusBadge(sub.status)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Requests Where I'm the Substitute */}
          <Card>
            <CardHeader>
              <CardTitle>{t('substitutions.sections.responsibilities')}</CardTitle>
            </CardHeader>
            <CardContent>
              {substitutionResponsibilities.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  {t('substitutions.noResponsibilities')}
                </p>
              ) : (
                <div className="space-y-4">
                  {substitutionResponsibilities.map((sub: Substitution) => (
                    <div
                      key={sub.id}
                      className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <UserGroupIcon className="h-5 w-5 text-gray-400" />
                            <span className="font-medium">
                              {sub.user?.first_name} {sub.user?.last_name}
                            </span>
                            <ArrowRightIcon className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-600">
                              {t('substitutions.requestedSubstitution')}
                            </span>
                            {getUserDepartment(sub.user) && (
                              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                                {getUserDepartment(sub.user)}
                              </span>
                            )}
                          </div>
                          {sub.user?.teams && sub.user.teams.length > 0 && (
                            <div className="ml-7 mb-2 flex flex-wrap gap-1">
                              {sub.user.teams.map(team => (
                                <span key={team.id} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                  {team.name}
                                </span>
                              ))}
                            </div>
                          )}
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-gray-500">{t('substitutionsPage.period')}</p>
                              <p className="font-medium">
                                {format(new Date(sub.leaveRequest?.start_date || ''), 'MMM d, yyyy')} - 
                                {format(new Date(sub.leaveRequest?.end_date || ''), 'MMM d, yyyy')}
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-500">{t('substitutions.days')}</p>
                              <p className="font-medium">
                                {sub.leaveRequest?.total_days || 0} {t('substitutions.days')}
                              </p>
                            </div>
                          </div>
                          {sub.area_of_responsibility && (
                            <div className="mt-2">
                              <p className="text-gray-500 text-sm">{t('substitutions.areaOfResponsibility')}</p>
                              <p className="text-sm">{sub.area_of_responsibility}</p>
                            </div>
                          )}
                          {sub.handover_notes && (
                            <div className="mt-2">
                              <p className="text-gray-500 text-sm">{t('substitutions.handoverNotes')}</p>
                              <p className="text-sm">{sub.handover_notes}</p>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          {getStatusBadge(sub.status)}
                          {sub.status === 'pending' && (
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedSubstitution(sub);
                                setResponseModalOpen(true);
                              }}
                            >
                              {t('substitutions.respond')}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Response Modal */}
      <Modal
        isOpen={responseModalOpen}
        onClose={() => {
          setResponseModalOpen(false);
          setSelectedSubstitution(null);
          setResponseComment('');
        }}
        title={t('substitutions.respondDialog.title')}
      >
        <div className="space-y-4">
          {selectedSubstitution && (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <p className="font-medium">
                  {selectedSubstitution.user?.first_name} {selectedSubstitution.user?.last_name}
                </p>
                {getUserDepartment(selectedSubstitution.user) && (
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                    {getUserDepartment(selectedSubstitution.user)}
                  </span>
                )}
              </div>
              
              {selectedSubstitution.user?.teams && selectedSubstitution.user.teams.length > 0 && (
                <div className="mt-1 mb-2 flex flex-wrap gap-1">
                  {selectedSubstitution.user.teams.map(team => (
                    <span key={team.id} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                      {team.name}
                    </span>
                  ))}
                </div>
              )}
              
              <p className="text-sm text-gray-600">
                {format(new Date(selectedSubstitution.leaveRequest?.start_date || ''), 'MMM d, yyyy')} - 
                {format(new Date(selectedSubstitution.leaveRequest?.end_date || ''), 'MMM d, yyyy')}
              </p>
              
              {selectedSubstitution.area_of_responsibility && (
                <p className="text-sm mt-2">
                  <span className="text-gray-500">{t('substitutions.respondDialog.area')}</span> {selectedSubstitution.area_of_responsibility}
                </p>
              )}
            </div>
          )}

          <Textarea
            label={t('substitutions.respondDialog.comment')}
            value={responseComment}
            onChange={(e) => setResponseComment(e.target.value)}
            rows={4}
            placeholder={t('substitutions.respondDialog.commentPlaceholder')}
          />

          <div className="flex gap-3">
            <Button
              onClick={() => handleRespond('accepted')}
              disabled={respondMutation.isPending}
              className="flex items-center gap-2"
            >
              <CheckCircleIcon className="h-5 w-5" />
              {t('substitutions.respondDialog.accept')}
            </Button>
            <Button
              variant="danger"
              onClick={() => handleRespond('rejected')}
              disabled={respondMutation.isPending}
              className="flex items-center gap-2"
            >
              <XCircleIcon className="h-5 w-5" />
              {t('substitutions.respondDialog.reject')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setResponseModalOpen(false);
                setSelectedSubstitution(null);
                setResponseComment('');
              }}
            >
              {t('substitutions.respondDialog.cancel')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Substitutions;